import React, { useState } from 'react';
import { Award, Users, Building, Globe, Youtube, Calendar, ExternalLink, MapPin, GraduationCap, Briefcase, FolderKanban, ArrowRight, Clock, Ruler } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import LeadCaptureModal from './LeadCaptureModal';
import { Badge } from './ui/badge';
import { ServiceCardsCarousel } from './ServiceCardsCarousel';
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from './ui/carousel';

const HeroSection = () => {
  const [isLeadModalOpen, setIsLeadModalOpen] = useState(false);
  const navigate = useNavigate();

  // Eventos BIM - ESTÁTICOS para garantir que sempre apareçam
  const events = [
    { 
      id: '1', 
      nome: 'BIM Forum Conference 2026', 
      data: '06 e 07 de Maio de 2026', 
      date_iso: '2026-05-07',
      local: 'São Paulo, Brasil', 
      url: 'https://bimforum.com.br',
      descricao: 'O maior evento de BIM da América Latina. Palestras, workshops e networking com os maiores especialistas do mercado.',
      country: 'Brasil'
    },
    { 
      id: '2', 
      nome: 'BIM World Paris', 
      data: 'Abril 2026', 
      date_iso: '2026-04-30',
      local: 'Paris, França', 
      url: 'https://www.bim-w.com/',
      descricao: 'O maior evento de BIM da Europa',
      country: 'França'
    },
    { 
      id: '3', 
      nome: 'Autodesk University', 
      data: 'Novembro 2026', 
      date_iso: '2026-11-30',
      local: 'Las Vegas, EUA', 
      url: 'https://www.autodesk.com/autodesk-university/',
      descricao: 'Conferência anual da Autodesk com foco em BIM e tecnologias de design',
      country: 'EUA'
    },
    { 
      id: '4', 
      nome: 'buildingSMART Summit', 
      data: 'Outubro 2026', 
      date_iso: '2026-10-31',
      local: 'A definir', 
      url: 'https://www.buildingsmart.org/',
      descricao: 'Evento global sobre openBIM e padrões internacionais',
      country: 'Internacional'
    },
    { 
      id: '5', 
      nome: 'Digital Construction Week', 
      data: 'Maio 2026', 
      date_iso: '2026-05-31',
      local: 'Londres, UK', 
      url: 'https://www.digitalconstructionweek.com/',
      descricao: 'Semana de construção digital com foco em inovação e tecnologia',
      country: 'UK'
    },
    { 
      id: '6', 
      nome: '5º BIM Summit Brasil', 
      data: '17 de Setembro de 2026', 
      date_iso: '2026-09-17',
      local: 'Belo Horizonte, MG', 
      url: 'https://doity.com.br/5-bim-summit-brasil',
      descricao: 'Evento internacional que reúne especialistas em BIM da América Latina e Europa. 5ª edição, integrando o 9º Seminário BIM, na UNIBH.',
      country: 'Brasil'
    },
    { 
      id: '7', 
      nome: '9º Congresso Internacional A Era BIM', 
      data: '24 a 26 de Novembro de 2026', 
      date_iso: '2026-11-24',
      local: 'Centro de Convenções Frei Caneca (4º andar), São Paulo/SP', 
      url: 'https://erabim.com/',
      descricao: 'Promovido pelo Sinaenco, é um dos maiores eventos da América Latina dedicados à transformação digital na AECO e à disseminação do BIM.',
      country: 'Brasil'
    }
  ];

  const today = new Date();
  const isPastEvent = (evento) => evento.date_iso && new Date(evento.date_iso) < today;
  // Próximos primeiro, realizados depois
  const sortedEvents = [...events].sort((a, b) => {
    const pa = isPastEvent(a) ? 1 : 0;
    const pb = isPastEvent(b) ? 1 : 0;
    if (pa !== pb) return pa - pb;
    return new Date(a.date_iso) - new Date(b.date_iso);
  });

  const stats = [
    { icon: Building, value: 'Desde 2005', label: 'Há mais de 20 anos formando profissionais em BIM no Brasil' },
    { icon: Users, value: '+18.000', label: 'Alunos Formados' },
    { icon: Clock, value: '+6.000h', label: 'de Consultoria em empresas e órgãos públicos' },
    { icon: Ruler, value: '+500 mil m²', label: 'de área modelada ou projetada em BIM' },
    { icon: Award, value: '98%', label: 'Satisfação' }
  ];

  const handleEventClick = (event) => {
    if (event.url) {
      window.open(event.url, '_blank', 'noopener,noreferrer');
    }
  };

  return (
    <>
    <section id="home" className="relative min-h-screen flex items-center justify-center overflow-hidden pt-20">
      {/* Background Image */}
      <div className="absolute inset-0">
        <img 
          src="https://images.unsplash.com/photo-1618385455730-2571c38966b7?w=1920&q=80" 
          alt="BIM Technology"
          className="w-full h-full object-cover"
        />
        {/* Red Overlay */}
        <div className="absolute inset-0 bg-gradient-to-br from-red-900/70 via-black/80 to-black/90"></div>
      </div>

      {/* Subtle pattern overlay */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-0 left-0 w-96 h-96 bg-red-600 rounded-full filter blur-3xl"></div>
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-red-800 rounded-full filter blur-3xl"></div>
      </div>

      <div className="container mx-auto px-4 lg:px-8 relative z-10">
        <div className="max-w-6xl mx-auto text-center">
          {/* Badge de autoridade */}
          <div className="flex justify-center mb-6 mt-4">
            <div className="inline-flex items-center gap-3 px-5 py-2 rounded-full bg-white/5 border border-white/10 backdrop-blur-sm text-sm text-gray-300">
              <span className="flex items-center gap-1.5">
                <Award size={15} className="text-red-500" /> Primeiro curso oficial de Revit no Brasil desde 2005
              </span>
              <span className="w-1 h-1 rounded-full bg-red-500" />
              <span className="flex items-center gap-1.5">
                <Users size={15} className="text-red-500" /> +18.000 alunos formados
              </span>
            </div>
          </div>

          {/* Heading principal (logo mantida apenas no cabeçalho) */}
          <h1 className="text-2xl md:text-4xl lg:text-5xl font-bold text-white mb-4 leading-tight">
            O maior e mais completo portal de serviços <span className="text-red-600">BIM</span> da América Latina
          </h1>

          {/* Subtitle */}
          <p className="text-lg md:text-xl text-gray-300 mb-12 max-w-3xl mx-auto leading-relaxed">
            Cursos e Formações Profissionais, consultoria estratégica, projetos e serviços BIM e conteúdo gratuito para profissionais e empresas que buscam excelência.
          </p>

          {/* Service Cards Navigation - Carrossel com efeito peek */}
          <div className="mb-16">
            <ServiceCardsCarousel onNavigate={navigate} />
            <p className="text-center text-gray-400 text-sm mt-6">Arraste para o lado ou use as setas para ver todos os serviços</p>
          </div>
        </div>
      </div>

      {/* Scroll Indicator */}
      <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
        <div className="w-6 h-10 border-2 border-white/30 rounded-full flex items-start justify-center p-2">
          <div className="w-1 h-3 bg-red-500 rounded-full"></div>
        </div>
      </div>

      {/* Lead Capture Modal */}
      <LeadCaptureModal 
        isOpen={isLeadModalOpen} 
        onClose={() => setIsLeadModalOpen(false)} 
        preSelectedService="Diagnóstico BIM"
      />
    </section>

    {/* Eventos BIM 2026 Section */}
    <section id="eventos" className="py-24 bg-black">
      <div className="container mx-auto px-4 lg:px-8">
        <div className="text-center mb-16">
          <Badge className="bg-purple-600/20 text-purple-400 border-purple-500/30 mb-4">Agenda</Badge>
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
            Eventos <span className="text-purple-500">BIM 2026</span>
          </h2>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            Os principais eventos de BIM ao redor do mundo para 2026. 
            Clique em um evento para visitar o site oficial.
          </p>
        </div>

        {/* Eventos em carrossel */}
        <Carousel
          opts={{ align: 'start', loop: true }}
          className="w-full max-w-6xl mx-auto"
        >
          <CarouselContent className="-ml-4">
            {sortedEvents.map((evento, index) => {
              const past = isPastEvent(evento);
              return (
                <CarouselItem
                  key={evento.id || index}
                  className="pl-4 basis-[85%] sm:basis-1/2 lg:basis-1/3"
                >
                  <div
                    onClick={() => handleEventClick(evento)}
                    className={`h-full rounded-xl p-6 border transition-all duration-300 hover:scale-[1.02] ${evento.url ? 'cursor-pointer' : ''} ${
                      past
                        ? 'bg-zinc-900/40 border-zinc-700/60 hover:border-zinc-500 grayscale-[0.2]'
                        : 'bg-gradient-to-br from-purple-900/20 to-zinc-900/50 border-purple-700/40 hover:border-purple-500'
                    }`}
                    data-testid={`evento-${index}`}
                  >
                    <div className="flex items-start justify-between mb-4">
                      <Calendar className={past ? 'text-gray-500' : 'text-purple-500'} size={32} />
                      <div className="flex items-center gap-2">
                        {past ? (
                          <Badge className="bg-zinc-700/50 text-gray-400 border-zinc-600">Realizado</Badge>
                        ) : (
                          <Badge className="bg-green-600/20 text-green-400 border-green-500/30">Em breve</Badge>
                        )}
                        {evento.url && (
                          <ExternalLink className="text-gray-500" size={16} />
                        )}
                      </div>
                    </div>
                    <h3 className={`font-bold text-lg mb-2 ${past ? 'text-gray-300' : 'text-white'}`}>{evento.nome}</h3>
                    <p className={`font-medium mb-1 ${past ? 'text-gray-500' : 'text-purple-400'}`}>{evento.data}</p>
                    <p className="text-gray-400 text-sm flex items-center gap-1">
                      <MapPin className="w-3.5 h-3.5" /> {evento.local}
                    </p>
                    {evento.descricao && (
                      <p className="text-gray-500 text-xs mt-3 line-clamp-3">{evento.descricao}</p>
                    )}
                  </div>
                </CarouselItem>
              );
            })}
          </CarouselContent>
          <CarouselPrevious
            className="left-1 md:-left-4 bg-white/10 border-white/20 text-white hover:bg-purple-600 hover:text-white hover:border-purple-600"
            data-testid="eventos-prev"
          />
          <CarouselNext
            className="right-1 md:-right-4 bg-white/10 border-white/20 text-white hover:bg-purple-600 hover:text-white hover:border-purple-600"
            data-testid="eventos-next"
          />
        </Carousel>

        <div className="text-center mt-12">
          <p className="text-gray-500 text-sm">
            * Datas sujeitas a confirmação pelos organizadores
          </p>
        </div>
      </div>
    </section>

    {/* Stats Section - abaixo dos Eventos BIM */}
    <section id="stats" className="py-16 bg-black">
      <div className="container mx-auto px-4 lg:px-8">
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-4 max-w-6xl mx-auto">
          {stats.map((stat, index) => {
            const Icon = stat.icon;
            return (
              <div
                key={index}
                className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-5 text-center hover:bg-white/10 transition-all duration-300 hover:scale-105"
                data-testid={`stat-card-${index}`}
              >
                <Icon className="text-red-500 mx-auto mb-3" size={28} />
                <div className="text-xl md:text-2xl font-bold text-white mb-2">{stat.value}</div>
                <div className="text-gray-400 text-xs">{stat.label}</div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
    </>
  );
};

export default HeroSection;